"use client";

import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Button} from "@/components/ui/button";
import {MAX_MEMORY_CHARS, type Mem0PublicItem} from "@/services/mem0/types";

type MemoryListItemProps = Readonly<{
   item: Mem0PublicItem;
   disabled?: boolean;
   onSave: (id: string, text: string) => Promise<boolean>;
   onDelete: (id: string) => Promise<boolean>;
}>;

function sourceLabel(tags: string, t: (key: string) => string): string {
   return tags === "ui" ? t("memory.sourceUi") : t("memory.sourceGpt");
}

function formatMemoryDate(createdAt: string | undefined, locale: string): string | null {
   if (!createdAt) return null;
   const date = new Date(createdAt);
   if (Number.isNaN(date.getTime())) return null;
   return new Intl.DateTimeFormat(locale, {dateStyle: "medium"}).format(date);
}

export default function MemoryListItem({item, disabled = false, onSave, onDelete}: MemoryListItemProps) {
   const {t, i18n} = useTranslation("ask");
   const [editing, setEditing] = useState(false);
   const [draft, setDraft] = useState(item.text);
   // Format after mount so the host timezone can't differ between SSR and hydration.
   const [mounted, setMounted] = useState(false);
   useEffect(() => setMounted(true), []);
   const addedDate = mounted ? formatMemoryDate(item.created_at, i18n.language) : null;

   const handleSave = async () => {
      const text = draft.trim();
      if (!text) return;
      const ok = await onSave(item.id, text);
      if (ok) setEditing(false);
   };

   return (
      <li className="border-200 bg-100/50 rounded-lg border px-3 py-2">
         {editing ? (
            <div className="flex flex-col gap-2">
               <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  disabled={disabled}
                  rows={3}
                  maxLength={MAX_MEMORY_CHARS}
                  className="border-input bg-transparent w-full rounded-md border px-3 py-2 text-sm"
                  aria-label={t("memory.edit")}
               />
               <div className="flex justify-end gap-2">
                  <Button
                     type="button"
                     variant="ghost"
                     size="sm"
                     disabled={disabled}
                     onClick={() => {
                        setDraft(item.text);
                        setEditing(false);
                     }}
                  >
                     {t("memory.cancel")}
                  </Button>
                  <Button type="button" size="sm" disabled={disabled || !draft.trim()} onClick={() => void handleSave()}>
                     {t("memory.save")}
                  </Button>
               </div>
            </div>
         ) : (
            <div className="flex flex-col gap-2">
               <p className="text-800 text-sm whitespace-pre-wrap">{item.text}</p>
               <div className="flex items-center justify-between gap-2">
                  <span className="text-500 flex items-center gap-1.5 text-xs font-medium">
                     {sourceLabel(item.tags, t)}
                     {addedDate && (
                        <>
                           <span aria-hidden="true">·</span>
                           <time
                              dateTime={item.created_at}
                              aria-label={t("memory.addedOn", {date: addedDate})}
                           >
                              {addedDate}
                           </time>
                        </>
                     )}
                  </span>
                  <div className="flex gap-1">
                     <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={() => {
                           setDraft(item.text);
                           setEditing(true);
                        }}
                     >
                        {t("memory.edit")}
                     </Button>
                     <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={() => void onDelete(item.id)}
                     >
                        {t("memory.delete")}
                     </Button>
                  </div>
               </div>
            </div>
         )}
      </li>
   );
}
